import type { ProjectDocument, ProjectNode, ProjectRoute } from '../types/project';
import type {
  DiagnosticIssue,
  DiagnosticReport,
  DiagnosticSummary,
} from '../types/diagnostics';
import { routeGeometryKey } from './routeGeometry';

function nodeNameMap(nodes: ProjectNode[]): Map<string, string> {
  return new Map(nodes.map((n) => [n.id, n.name]));
}

function isZeroLengthRoute(
  route: ProjectRoute,
  positions: Map<string, { x: number; y: number }>,
): boolean {
  if ((route.waypoints?.length ?? 0) > 0) return false;
  const from = positions.get(route.from_node_id);
  const to = positions.get(route.to_node_id);
  if (!from || !to) return false;
  return Math.abs(from.x - to.x) < 0.5 && Math.abs(from.y - to.y) < 0.5;
}

function pickRouteToKeep(routes: ProjectRoute[]): ProjectRoute {
  return [...routes].sort((a, b) => {
    const ai = a.output_index ?? 0;
    const bi = b.output_index ?? 0;
    if (ai !== bi) return ai - bi;
    return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
  })[0];
}

/** Fast client-side preview; server scan remains source of truth for apply. */
export function scanRouteDiagnosticsClient(
  document: ProjectDocument,
): DiagnosticReport {
  const names = nodeNameMap(document.nodes);
  const positions = new Map(
    document.nodes.map((n) => [n.id, { x: n.x, y: n.y }]),
  );
  const nodeIds = new Set(document.nodes.map((n) => n.id));
  const issues: DiagnosticIssue[] = [];
  const summary: DiagnosticSummary = {
    duplicate_groups: 0,
    duplicate_routes: 0,
    zero_length_groups: 0,
    dangling_routes: 0,
    isolated_nodes: 0,
    auto_fixable_count: 0,
  };

  for (const route of document.routes) {
    const missing: string[] = [];
    if (!nodeIds.has(route.from_node_id)) missing.push(route.from_node_id);
    if (!nodeIds.has(route.to_node_id)) missing.push(route.to_node_id);
    if (missing.length > 0) {
      summary.dangling_routes += 1;
      issues.push({
        code: 'ROUTE_DANGLING_ENDPOINT',
        severity: 'error',
        message: `Route '${route.name}' references missing node(s).`,
        route_ids: [route.id],
        route_names: [route.name],
        node_ids: missing,
        node_names: missing.map((id) => names.get(id) ?? id),
        suggested_action: 'delete_route',
        auto_fixable: false,
      });
    }
  }

  const groups = new Map<string, ProjectRoute[]>();
  for (const route of document.routes) {
    const key = routeGeometryKey(route);
    const list = groups.get(key) ?? [];
    list.push(route);
    groups.set(key, list);
  }

  for (const route of document.routes) {
    const group = groups.get(routeGeometryKey(route));
    if (group?.length === 1 && isZeroLengthRoute(route, positions)) {
      summary.zero_length_groups += 1;
      issues.push({
        code: 'ROUTE_ZERO_LENGTH',
        severity: 'warning',
        message: `Route '${route.name}' has zero length.`,
        route_ids: [route.id],
        route_names: [route.name],
        node_ids: [route.from_node_id, route.to_node_id],
        node_names: [
          names.get(route.from_node_id) ?? route.from_node_id,
          names.get(route.to_node_id) ?? route.to_node_id,
        ],
        suggested_action: 'delete_route',
        auto_fixable: true,
      });
      summary.auto_fixable_count += 1;
    }
  }

  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const zeroLen = group.every((r) => isZeroLengthRoute(r, positions));
    const code = zeroLen ? 'ROUTE_ZERO_LENGTH' : 'ROUTE_DUPLICATE_GEOMETRY';
    if (zeroLen) summary.zero_length_groups += 1;
    else summary.duplicate_groups += 1;
    summary.duplicate_routes += group.length - 1;

    const keep = pickRouteToKeep(group);
    const toRemove = group.filter((r) => r.id !== keep.id);
    summary.auto_fixable_count += 1;
    issues.push({
      code,
      severity: 'warning',
      message: `${group.length} routes share the same path. Keep '${keep.name}', remove ${toRemove.length} duplicate(s).`,
      route_ids: toRemove.map((r) => r.id),
      route_names: toRemove.map((r) => r.name),
      node_ids: [keep.from_node_id, keep.to_node_id],
      node_names: [
        names.get(keep.from_node_id) ?? keep.from_node_id,
        names.get(keep.to_node_id) ?? keep.to_node_id,
      ],
      suggested_action: 'delete_duplicate_keep_first',
      auto_fixable: true,
      keep_route_id: keep.id,
    });
  }

  for (const node of document.nodes) {
    const incoming = document.routes.some((r) => r.to_node_id === node.id);
    const outgoing = document.routes.some((r) => r.from_node_id === node.id);
    if (!incoming && !outgoing) {
      summary.isolated_nodes += 1;
      issues.push({
        code: 'NODE_ISOLATED',
        severity: 'info',
        message: `Node '${node.name}' has no incoming or outgoing routes.`,
        route_ids: [],
        route_names: [],
        node_ids: [node.id],
        node_names: [node.name],
        suggested_action: 'open_node',
        auto_fixable: false,
      });
    }
  }

  return { issues, summary };
}
